import { isApiError } from './error.js';
import { EtService } from './et.service.js';
import { fetchData } from './fetch-data.js';
import { OAuthSession } from './oauth-session.js';

export type Target = '3.0' | '4.0';

export type CreateRoute = {
  url_path: string;
  target: Target;
  wildcard: boolean;
};

export type Route = CreateRoute & {
  id: number;
  created_at?: string;
  updated_at?: string;
};

export type Column = {
  label: string;
  sortable: boolean;
  field?: string;
  formatter?: (rawFieldContent: string | number | boolean) => unknown;
};

export type SortDirection = 'ASC' | 'DESC';

type ListParams = {
  sortBy?: string;
  sortDirection?: string;
  limit: number;
  offset?: number;
  target?: string;
};

export class RoutingService {
  public async createRoute(newRoute: CreateRoute) {
    const username = OAuthSession.getUserName();

    if (!username) {
      throw new Error('Route kann nicht erstellt werden. Unbekannter Nutzer');
    }

    const response = await fetchData('/api/routings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...newRoute, username })
    });

    if (response.status > 400) {
      const error = JSON.parse(await response.json());
      if (isApiError(error)) {
        if (error.code === 20) {
          throw new Error(`Es existiert bereits eine Route für '${error.message}'`);
        }
        if (error.code === 30) {
          const message = error.message.includes(',')
            ? `Die folgenden Werte sind ungültig: '${error.message}'`
            : `Der folgende Wert ist ungültig: '${error.message}'`;
          throw new Error(message);
        }
      }

      throw new Error('Es ist ein Fehler beim Erstellen der Route aufgetreten.');
    }
  }

  public async updateRoute(changedRoute: Route) {
    const username = OAuthSession.getUserName();

    if (!username) {
      throw new Error('Route kann nicht aktualisiert werden. Unbekannter Nutzer');
    }

    const response = await fetchData(`/api/routings/${changedRoute.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...changedRoute, username })
    });

    if (response.status >= 400) {
      const error = JSON.parse(await response.json());
      if (isApiError(error)) {
        if (error.code === 20) {
          throw new Error(`Es existiert bereits eine Route für '${error.message}'`);
        }
        if (error.code === 30) {
          const message = error.message.includes(',')
            ? `Die folgenden Werte sind ungültig: '${error.message}'`
            : `Der folgende Wert ist ungültig: '${error.message}'`;
          throw new Error(message);
        }
      }

      throw new Error(await response.json());
    }
  }

  public async delete(routeId: number) {
    const username = OAuthSession.getUserName();

    if (!username) {
      throw new Error('Die Route kann nicht gelöscht werden. Unbekannter Nutzer');
    }

    const response = await fetchData(`/api/routings/${routeId}`, {
      method: 'DELETE',
      body: JSON.stringify({ username })
    });

    if (response.status < 200 || response.status >= 400) {
      throw new Error(
        `Es ist ein Fehler beim Löschen der Route mit der ID ${routeId} aufgetreten.`
      );
    }
  }

  public async getList(params?: ListParams) {
    const searchParams = params
      ? `&${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`
      : '';

    const response = await fetchData(`/api/routings?publishState=all${searchParams}`);

    if (response.status >= 200 && response.status < 400) {
      return await response.json();
    }

    throw new Error('Es ist ein Fehler beim Laden der Routings aufgetreten.');
  }

  public async publish() {
    const response = await fetchData(`/api/routings`, {
      method: 'PATCH',
      body: JSON.stringify({ requestType: 'publish' })
    });

    if (response.status < 200 || response.status >= 400) {
      throw new Error(`Es ist ein Fehler beim Aktualisieren der Datenbank aufgetreten.`);
    }

    try {
      const etService = new EtService();
      await etService.publish('routings');
    } catch (e) {
      throw new Error(`Es ist ein Fehler beim Anstoßen der Veröffentlichung aufgetreten: ${e}`);
    }
  }
}
